// charterMeta — the Contract surface's PRESENTATION derivations over the shared
// charter registry (@ems-app/schemas/invoicing/charter — the single legality
// source: fields · groups · the allowed-natures matrix). This module adds only
// what display needs: group ordering, the band-family display metadata (unit
// label key + formatValue dimension), and the source-tile model the Terms
// surface + config form + peek share. No rules live here — a legality question
// always resolves through the registry, and the module-load asserts below keep
// these display lists from silently drifting off it.

import {
  BANDED_SOURCES,
  BAND_FAMILY_COLUMNS,
  BAND_FAMILY_FIELD,
  CHARTER_FIELDS,
  CHARTER_FIELD_SPECS,
  CHARTER_SOURCES,
  CORE_FIELDS,
  ENV_FIELDS,
  renewablesEngaged,
  type BandFamily,
  type CharterField,
  type CharterFieldGroup,
  type CharterNature,
  type CharterSource,
} from '@ems-app/schemas/invoicing/charter';
import type { ContractualConfig } from '@ems-app/schemas/invoicing/responses';
import type { Dimension } from '../../lib/formatValue.js';

/** The effective charter shape every surface projects through. */
export type Natures = Record<CharterField, CharterNature>;

// ── groups, in display order ─────────────────────────────────────────────────
const GROUP_ORDER: readonly CharterFieldGroup[] = ['designation', 'band', 'composition', 'scalar', 'kpi'];

export const FIELDS_BY_GROUP: ReadonlyMap<CharterFieldGroup, readonly CharterField[]> = new Map(
  GROUP_ORDER.map((g) => [g, CHARTER_FIELDS.filter((f) => CHARTER_FIELD_SPECS[f].group === g)]),
);

export { CHARTER_FIELD_SPECS };
export type { CharterField, CharterFieldGroup, CharterNature };

// ── band-family display metadata ─────────────────────────────────────────────
export const BAND_FAMILIES_ORDER: readonly BandFamily[] = ['load', 'grid_availability', 'solar', 'cooling'];

/** formatValue dimension per family (ADR-030 scaler); the unit LABEL is i18n
 *  (invoicing:bands.units.<family>) — SI symbol Latin, period word localized. */
export const FAMILY_DIMENSION: Record<BandFamily, Dimension> = {
  load: 'power',
  grid_availability: 'duration_h',
  solar: 'power',
  cooling: 'power',
};

/** The charter band field owning each family (load's is locked Priced), and the
 *  config row's (band_id, band_none) column pair per family — both REGISTRY-owned
 *  (the import/export column-model projects through the same tables), re-exported
 *  here under the surface's established names with the display-side narrowing. */
export const FAMILY_CHARTER_FIELD: Record<BandFamily, CharterField> = BAND_FAMILY_FIELD;

export const FAMILY_COLS = BAND_FAMILY_COLUMNS as Record<
  BandFamily,
  { id: keyof ContractualConfig; none: keyof ContractualConfig | null }
>;

// ── charter projections (which fields a surface renders) ─────────────────────
export function engagedSources(natures: Natures): readonly SourceMeta[] {
  return SOURCES.filter((s) => natures[s.requiredField] !== 'off');
}
/** Sources carrying any PRICED address term — presence priced, or the band
 *  priced (a priced band under a committed presence keeps its source in the
 *  address). The composition glyph, its tooltip, and the composition group
 *  lens read THIS set; a commitment-only source lives in the commitments
 *  rollup instead (the glyph-vs-rollup split). */
export function pricedSources(natures: Natures): readonly SourceMeta[] {
  return SOURCES.filter(
    (s) => natures[s.requiredField] === 'priced' || (s.bandField !== undefined && natures[s.bandField] === 'priced'),
  );
}
export function pricedFamilies(natures: Natures): BandFamily[] {
  return BAND_FAMILIES_ORDER.filter((f) => natures[FAMILY_CHARTER_FIELD[f]] === 'priced');
}
export function committedFamilies(natures: Natures): BandFamily[] {
  return BAND_FAMILIES_ORDER.filter((f) => natures[FAMILY_CHARTER_FIELD[f]] === 'commitment');
}
export function valueFieldsOf(natures: Natures, nature: CharterNature): ValueFieldMeta[] {
  return VALUE_FIELD_DIM.filter((f) => natures[f.key] === nature);
}

// ── the absence grammar's applicability rule ─────────────────────────────────
// STRUCTURAL absence is silence: a value field whose carrier isn't in this
// config's composition never renders and never counts. Source-owned fields
// follow their source's presence; the core kit + CO₂ ceilings follow "any
// energy source present" (a service-only cell demands no ESCO kit); RER
// follows a renewable being present.
export function isApplicable(config: ContractualConfig, key: CharterField): boolean {
  const src = SOURCES.find((s) => (s.valueFields as readonly CharterField[]).includes(key));
  if (src) return config[src.requiredField as keyof ContractualConfig] === true;
  if (key === 'rer_min_pct') {
    return SOURCES.filter((s) => s.key === 'solar' || s.key === 'wind').some(
      (s) => config[s.requiredField as keyof ContractualConfig] === true,
    );
  }
  if ((CORE_FIELDS as readonly CharterField[]).includes(key) || (ENV_FIELDS as readonly CharterField[]).includes(key)) {
    return SOURCES.some((s) => config[s.requiredField as keyof ContractualConfig] === true);
  }
  return true;
}

export function applicableValueFields(
  natures: Natures,
  config: ContractualConfig,
  nature: CharterNature,
): ValueFieldMeta[] {
  return valueFieldsOf(natures, nature).filter((f) => isApplicable(config, f.key));
}

// ── the source-tile model (config form + peek + drawers) ─────────────────────
// The dependency table itself is REGISTRY-OWNED (CHARTER_SOURCES in charter.ts, the
// same table the cross-field laws judge); this module re-exports it under the
// surface's established names. Load is NOT a source tile — every site has a
// load; its band renders as the always-priced picker.
export type SourceMeta = CharterSource;
export const SOURCES: readonly SourceMeta[] = CHARTER_SOURCES;
export { renewablesEngaged };

/** The two value-term clusters — the core kit (power system first, then the
 *  battery trio) and the outcome metrics — are REGISTRY-owned display orders
 *  (the peek cluster, the form value inputs, and the import/export column set
 *  all read the same list). Re-exported here under the surface's names. */
export { CORE_FIELDS, ENV_FIELDS };

// Sanity: the banded subset mirrors the registry's BANDED_SOURCES (one source of
// truth; this module only re-shapes for display).
if (SOURCES.filter((s) => s.bandField).length !== BANDED_SOURCES.length) {
  throw new Error('charterMeta SOURCES out of sync with the registry BANDED_SOURCES');
}

// Coverage: every VALUE term (scalar/KPI) in the registry is homed in exactly
// ONE Terms list (a source's valueFields · CORE_FIELDS · ENV_FIELDS). A field
// added to the registry but missed here would be legal via the charter API yet
// invisible and un-editable in the Terms UI — fail fast at module load (every
// page test imports this module, so CI catches it before any human does).
{
  const homed = [...SOURCES.flatMap((s) => s.valueFields), ...CORE_FIELDS, ...ENV_FIELDS];
  const homedSet = new Set(homed);
  if (homed.length !== homedSet.size) throw new Error('charterMeta: a value term is homed twice');
  const registryValueFields = [...(FIELDS_BY_GROUP.get('scalar') ?? []), ...(FIELDS_BY_GROUP.get('kpi') ?? [])];
  const unhomed = registryValueFields.filter((f) => !homedSet.has(f));
  const alien = homed.filter((f) => !registryValueFields.includes(f));
  if (unhomed.length > 0 || alien.length > 0) {
    throw new Error(
      `charterMeta value-term lists out of sync with the registry — unhomed: [${unhomed.join(', ')}] · not in registry: [${alien.join(', ')}]`,
    );
  }
}

// ── scalar/KPI field metadata (the config form's value inputs) ───────────────
// integer = whole-number input (decimals 0); the rest allow 2 decimals (wire
// bounds live in the request schema — the form schema mirrors them loosely and
// the service is authoritative).
export const INTEGER_VALUE_FIELDS: ReadonlySet<CharterField> = new Set<CharterField>([
  'battery_capacity_min_ah',
  'generator_units_min',
  'cooling_units_min',
  'solar_production_min_kwh_per_year',
  'diesel_max_l_per_year',
  'grid_import_max_kwh_per_year',
  'cooling_energy_max_kwh_per_year',
  'co2_energy_max_kg_per_year',
  'co2_lca_max_kg_per_year',
]);

/** Scalar/KPI value fields in registry order, per group (excludes
 *  battery_chemistry, which renders as a Select). */
export const NUMERIC_SCALAR_FIELDS: readonly CharterField[] = (FIELDS_BY_GROUP.get('scalar') ?? []).filter(
  (f) => f !== 'battery_chemistry',
);
export const KPI_FIELDS: readonly CharterField[] = FIELDS_BY_GROUP.get('kpi') ?? [];

// ── value-field display metadata (list rollups · peek · form inputs) ─────────
export interface ValueFieldMeta {
  key: CharterField & keyof ContractualConfig;
  /** formatValue dimension; 'text' = an enum rendered through its own labels. */
  dim: Dimension | 'text';
}

/** Every value term (scalar + KPI) with its format dimension, in the surfaces'
 *  display order. Labels resolve through i18n (invoicing:charter.fields.*). */
export const VALUE_FIELD_DIM: readonly ValueFieldMeta[] = [
  { key: 'power_system_capacity_min_kw', dim: 'power' },
  { key: 'battery_chemistry', dim: 'text' },
  { key: 'battery_capacity_min_ah', dim: 'battery_charge' },
  { key: 'battery_autonomy_min_h', dim: 'duration_h' },
  { key: 'generator_capacity_min_kva', dim: 'apparent_power' },
  { key: 'generator_units_min', dim: 'count' },
  { key: 'grid_capacity_min_kw', dim: 'power' },
  { key: 'cooling_units_min', dim: 'count' },
  { key: 'wind_capacity_min_kw', dim: 'power' },
  { key: 'rer_min_pct', dim: 'ratio_pct' },
  { key: 'solar_production_min_kwh_per_year', dim: 'energy' },
  { key: 'diesel_max_l_per_year', dim: 'fuel_volume' },
  { key: 'grid_import_max_kwh_per_year', dim: 'energy' },
  { key: 'runtime_max_h_per_day', dim: 'duration_h' },
  { key: 'cooling_energy_max_kwh_per_year', dim: 'energy' },
  { key: 'co2_energy_max_kg_per_year', dim: 'co2_mass' },
  { key: 'co2_lca_max_kg_per_year', dim: 'co2_mass' },
] as const;

// Coverage: VALUE_FIELD_DIM must be exactly the registry's scalar+KPI set —
// a registry field missing here would be legal via the API yet invisible in
// the rollups/peek/form value inputs (same fail-fast as the homing assert).
{
  const dimKeys = new Set<CharterField>(VALUE_FIELD_DIM.map((f) => f.key));
  const registryValueFields = [...(FIELDS_BY_GROUP.get('scalar') ?? []), ...(FIELDS_BY_GROUP.get('kpi') ?? [])];
  if (dimKeys.size !== VALUE_FIELD_DIM.length) throw new Error('charterMeta VALUE_FIELD_DIM: a key is listed twice');
  const missing = registryValueFields.filter((f) => !dimKeys.has(f));
  const alien = [...dimKeys].filter((f) => !registryValueFields.includes(f));
  if (missing.length > 0 || alien.length > 0) {
    throw new Error(
      `charterMeta VALUE_FIELD_DIM out of sync with the registry — missing: [${missing.join(', ')}] · not in registry: [${alien.join(', ')}]`,
    );
  }
}
